import type { PatientVital } from '@/types/vitals-api';
import { VITAL_CODES } from './vital-codes';

/**
 * Series extraction for the vitals sparkline. The polyline itself is now the
 * Signal DS `Sparkline` atom (graduated from this file — `owner ruling
 * 2026-09-23: the DS renders, the app passes props`); this file keeps only
 * the data-shaping helpers `vital-cell.tsx` needs to build `data` for it.
 */

/**
 * Extract the numeric series for a given vital code from the
 * patient-wide history. Returns values in chronological order
 * (oldest → newest), filtered to entries with a parseable numeric
 * value. Mirrors Flutter `VitalUiData.history(vitalHistoryRequest)`
 * in `vital_data.dart:170-209`, minus the BMI special case (we
 * compute that separately via `vital-derive.ts`).
 *
 * Empty values, non-numeric values, and non-current-code entries are
 * filtered out. The result is what the sparkline consumes.
 */
export function extractVitalSeries(
  code: string,
  history: PatientVital[],
): number[] {
  return history
    .filter((r) => r.code?.code === code)
    .map((r) => ({
      value: r.quantity?.value,
      date: r.effectiveDateTime ?? r.createdDate ?? '',
    }))
    .filter(
      (p) => p.value != null && p.value.trim() !== '',
    )
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    .map((p) => Number(p.value))
    .filter((n) => Number.isFinite(n));
}

/**
 * BMI special case (Flutter parity — `vital_data.dart:170-191`).
 * BMI history is computed from height/weight pairs PER ENCOUNTER —
 * BMI isn't stored as its own row in history. For each encounter
 * with BOTH a height and weight, compute the BMI.
 */
export function extractBmiSeries(history: PatientVital[]): number[] {
  const heights = new Map<string, number>();
  const weights = new Map<string, number>();

  for (const r of history) {
    const code = r.code?.code;
    const value = Number(r.quantity?.value);
    const encounter = r.encounter ?? '';
    if (!encounter || !Number.isFinite(value)) continue;
    if (code === VITAL_CODES.HEIGHT) heights.set(encounter, value);
    if (code === VITAL_CODES.WEIGHT) weights.set(encounter, value);
  }

  const points: { date: string; bmi: number }[] = [];
  for (const [encounter, weight] of weights) {
    const height = heights.get(encounter);
    if (!height || height === 0 || !weight) continue;
    const bmi = Math.round((weight * 10000) / (height * height));
    // Reuse one of the rows' date for ordering.
    const date =
      history.find((r) => r.encounter === encounter)?.effectiveDateTime ??
      history.find((r) => r.encounter === encounter)?.createdDate ??
      '';
    points.push({ date, bmi });
  }

  return points
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    .map((p) => p.bmi);
}
